/**
 * Auth Status Section — Sidebar & Mobile Navigation Auth Controls
 *
 * Renders login/logout button and user profile based on auth state.
 * Three visual states:
 *   1. Loading — spinner while OIDC initializes (checks sessionStorage)
 *   2. Authenticated — user initial circle + name + role badge + logout
 *   3. Unauthenticated — "Sign in" button that redirects to Keycloak
 *
 * Responsive: `isExpanded` controls full-text vs icon-only rendering,
 * matching the same pattern used by the API status dot and mode badge
 * in Sidebar.tsx.
 */

import { LogIn, LogOut, Loader2, Shield } from "lucide-react";
import { useNavigate } from "react-router";
import { useAuth } from "../../providers/AuthProvider";
import { cn } from "../../lib/utils";

interface AuthStatusSectionProps {
  isExpanded: boolean;
}

// Demo-only: auth/backend decommissioned. Typed as `boolean` (not the literal
// `true`) so the sign-in/logout UI below stays reachable for the type-checker —
// it is preserved for when auth returns, just gated off.
const DEMO_ONLY: boolean = true;

export function AuthStatusSection({ isExpanded }: AuthStatusSectionProps) {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const navigate = useNavigate();

  // Demo-only: render nothing so there is no sign-in/logout control that could
  // navigate to a login page / dead Keycloak.
  if (DEMO_ONLY) return null;

  // State 1: OIDC library loading (checking sessionStorage for existing token)
  // Brief (~100ms) — prevents flash of "Sign in" for already-authenticated users.
  if (isLoading) {
    return (
      <div
        className={cn(
          "flex items-center text-xs py-1",
          isExpanded ? "gap-2 px-3" : "justify-center",
        )}
      >
        <Loader2 className="w-4 h-4 text-text-muted animate-spin shrink-0" />
        {isExpanded && <span className="text-text-muted">Loading...</span>}
      </div>
    );
  }

  // State 2: Authenticated — show user profile + logout
  if (isAuthenticated && user) {
    const initial = user.name.charAt(0).toUpperCase();
    const isAdmin = user.roles.includes("admin");

    return (
      <div
        className={cn(
          "flex items-center text-xs py-1",
          isExpanded ? "gap-2 px-3" : "justify-center gap-1.5",
        )}
      >
        {/* User initial circle */}
        <span
          className="flex items-center justify-center w-6 h-6 rounded-full bg-accent-cyan/20 text-accent-cyan text-xs font-semibold shrink-0"
          title={!isExpanded ? `Signed in as ${user.name}` : undefined}
        >
          {initial}
        </span>

        {isExpanded && (
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-text-primary truncate text-xs font-medium">
                {user.name}
              </span>
              {isAdmin && (
                <span className="flex items-center gap-0.5 px-1 py-px rounded text-[10px] font-medium bg-accent-green/20 text-accent-green">
                  <Shield className="w-2.5 h-2.5" />
                  Admin
                </span>
              )}
            </div>
          </div>
        )}

        {/* Logout button */}
        <button
          type="button"
          onClick={logout}
          className={cn(
            "text-text-muted hover:text-accent-red transition-colors shrink-0",
            isExpanded ? "p-0.5" : "p-1",
          )}
          title="Sign out"
          aria-label="Sign out"
        >
          <LogOut className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  // State 3: Unauthenticated — navigate to /login page.
  // Previously called login() directly (Keycloak redirect). Now we show a
  // dedicated login page with Google + Email options (#71, F.1.6).
  return (
    <button
      type="button"
      onClick={() => navigate("/login")}
      className={cn(
        "flex items-center text-xs py-1",
        "text-text-muted hover:text-accent-cyan transition-colors",
        isExpanded ? "gap-2 px-3 w-full" : "justify-center",
      )}
      title={!isExpanded ? "Sign in" : undefined}
      aria-label="Sign in"
    >
      <LogIn className="w-4 h-4 shrink-0" />
      {isExpanded && <span className="font-medium">Sign in</span>}
    </button>
  );
}
